
import { useNavigate } from 'react-router-dom';

export default function CampaignSuccessSection() {
  const successMetrics = [
    {
      title: 'Campaign Performance Analysis',
      icon: 'ri-bar-chart-line',
      description: 'ROI optimization through real-time exposure, reach, and engagement measurement',
      features: [
        '24/7 Real-time monitoring',
        'Detailed demographic analysis',
        'Competitive performance comparison',
        'Customized reporting'
      ]
    },
    {
      title: 'Strategic Location Selection',
      icon: 'ri-map-pin-line',
      description: 'Securing premium locations with highest target customer traffic',
      features: [
        'Traffic big data analysis',
        'Population density optimization',
        'Brand visibility maximization',
        'Cost-effectiveness analysis'
      ]
    },
    {
      title: 'Creative Optimization',
      icon: 'ri-palette-line',
      description: 'Customized advertising creation tailored to regional culture and language',
      features: [
        'Localized message development',
        'Cultural sensitivity consideration',
        'A/B testing implementation',
        'Multi-language support service'
      ]
    },
    {
      title: 'Regulatory Compliance Management',
      icon: 'ri-shield-check-line',
      description: 'Perfect compliance with advertising regulations and permit requirements in each country',
      features: [
        'Full compliance with local regulations',
        'Permit process management',
        'Proactive risk management',
        'Compliance assurance'
      ]
    }
  ];

  const caseStudies = [
    {
      client: 'Global Luxury Brand',
      location: 'Dubai, UAE',
      result: '340% increase in brand awareness',
      campaign: 'Premium locations around Dubai Mall & Burj Khalifa',
      image: 'https://public.readdy.ai/ai/img_res/9adf7360a73d996a7576177a9485c5d5.jpg'
    },
    {
      client: 'Tech Startup',
      location: 'Tokyo, Japan',
      result: '250% increase in app downloads',
      campaign: 'Shibuya Crossing & Shinjuku Station digital signage',
      image: 'https://readdy.ai/api/search-image?query=Tech%20startup%20digital%20billboard%20campaign%20in%20Tokyo%20Shibuya%20crossing%2C%20modern%20LED%20display%20with%20vibrant%20colors%2C%20busy%20urban%20intersection%20with%20neon%20lights%2C%20contemporary%20Japanese%20cityscape%20with%20digital%20advertising%20screens&width=400&height=300&seq=tokyo-tech-campaign&orientation=landscape'
    },
    {
      client: 'Automotive Brand',
      location: 'Riyadh, Saudi Arabia',
      result: '180% increase in dealer visits',
      campaign: 'Large billboards on King Fahd Road & Olaya District',
      image: 'https://readdy.ai/api/search-image?query=Automotive%20brand%20billboard%20campaign%20in%20Riyadh%20Saudi%20Arabia%2C%20large%20highway%20billboard%20with%20luxury%20car%20advertisement%2C%20modern%20Middle%20Eastern%20cityscape%20with%20wide%20roads%2C%20premium%20outdoor%20advertising%20with%20desert%20urban%20landscape&width=400&height=300&seq=riyadh-auto-campaign&orientation=landscape'
    }
  ];

  const navigate = useNavigate();

  const handleConsultationClick = () => {
    navigate('/portal?modal=consultation');
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 xl:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 서비스 우수성 섹션 */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-900 mb-3 sm:mb-4">
            Professional Services for Campaign Success
          </h2>
          <p className="text-sm sm:text-base lg:text-xl text-gray-600 font-light max-w-3xl mx-auto">
            DHB Group's differentiated services that maximize advertising effectiveness through data-driven strategies and local expertise
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-12 sm:mb-16 lg:mb-20">
          {successMetrics.map((metric, index) => (
            <div key={index} className="bg-white rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-2 sm:mb-3 lg:mb-4">
                <i className={`${metric.icon} text-blue-600 text-base sm:text-lg lg:text-xl`}></i>
              </div>
              
              <h3 className="text-sm sm:text-base lg:text-lg font-medium text-gray-900 mb-1 sm:mb-2 lg:mb-3">
                {metric.title}
              </h3>
              <p className="text-gray-600 font-light mb-2 sm:mb-3 lg:mb-4 text-xs sm:text-sm">
                {metric.description}
              </p>
              
              <ul className="space-y-1 sm:space-y-2">
                {metric.features.map((feature, idx) => (
                  <li key={idx} className="text-xs sm:text-sm text-gray-500 flex items-center">
                    <i className="ri-check-line text-green-500 mr-2 flex-shrink-0"></i>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* 성공 사례 섹션 */}
        <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8">
          <div className="text-center mb-6 sm:mb-8 lg:mb-12">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-light text-gray-900 mb-2 sm:mb-3 lg:mb-4">
              Verified Success Stories
            </h3>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 font-light">
              Discover the remarkable achievements of our actual clients
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="group">
                <div className="relative overflow-hidden rounded-lg mb-3 sm:mb-4">
                  <img
                    src={study.image}
                    alt={study.campaign}
                    className="w-full h-32 sm:h-40 lg:h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-2 sm:bottom-3 lg:bottom-4 left-2 sm:left-3 lg:left-4 text-white">
                    <p className="text-xs sm:text-sm font-medium">{study.location}</p>
                  </div>
                </div>
                
                <div className="space-y-1 sm:space-y-2">
                  <h4 className="font-medium text-gray-900 text-sm sm:text-base">{study.client}</h4>
                  <p className="text-xs sm:text-sm text-gray-600">{study.campaign}</p>
                  <div className="bg-green-50 rounded-lg p-2 sm:p-3">
                    <p className="text-green-700 font-medium text-xs sm:text-sm">
                      <i className="ri-arrow-up-line mr-1"></i>
                      {study.result}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-6 sm:mt-8 lg:mt-12">
            <div className="bg-blue-50 rounded-lg p-3 sm:p-4 lg:p-6 max-w-2xl mx-auto">
              <h4 className="text-sm sm:text-base lg:text-lg font-medium text-gray-900 mb-1 sm:mb-2">
                Your brand can achieve these results too
              </h4>
              <p className="text-gray-600 font-light mb-2 sm:mb-3 lg:mb-4 text-xs sm:text-sm lg:text-base">
                We'll propose a customized campaign strategy through free consultation
              </p>
              <button 
                onClick={handleConsultationClick}
                className="bg-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm sm:text-base lg:text-lg whitespace-nowrap cursor-pointer"
              >
                Schedule Free Consultation
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
